import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import {
  Container,
  Flex,
  IconButton,
  ScrollArea,
  Text,
} from "@radix-ui/themes";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";
import {
  CreateSampleConfigurationsParams,
  CreateSampleFormParams,
  useSamplingApi,
} from "@/v2/entities/sampling/Sampling.api";
import { SampleForm } from "@/v2/features/sampling/SampleForm";

function CreateSamplePage() {
  const navigate = useNavigate();
  const samplingApi = useSamplingApi();

  const [loading, setLoading] = useState(false);

  const { mutateAsync: createSampleForm } = useMutation(
    samplingApi.createSampleForm
  );

  const { mutateAsync: createSampleConfigurations } = useMutation(
    samplingApi.createSampleConfigurations
  );

  const create = useCallback(
    async ({
      configurations,
      ...data
    }: CreateSampleFormParams & CreateSampleConfigurationsParams) => {
      const res = await createSampleForm(data);

      if (configurations.length > 0) {
        await createSampleConfigurations({
          configurations: configurations.map(({ id, ...configuration }) => ({
            ...configuration,
            sampleFormId: res.id,
          })),
        });
      }

      return res;
    },
    [createSampleForm, navigate]
  );

  const handleSubmit = useCallback(
    async (data: CreateSampleFormParams & CreateSampleConfigurationsParams) => {
      setLoading(true);
      toast.promise(create(data), {
        loading: "Creating scorecard...",
        success: ({ name }) => {
          setLoading(false);
          navigate("/quality/scorecards");

          return {
            message: `${name} created successfully`,
          };
        },
        error: "Failed to create scorecard",
      });
    },
    [create, navigate]
  );

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 2000,
        background: "var(--backgroundDefault)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Flex
        justify="start"
        align="center"
        py={"4"}
        px={"8"}
        gap={"4"}
        style={{
          borderBottom: "1px solid var(--gray-4)",
          boxShadow: "var(--shadow-1)",
        }}
      >
        <IconButton
          variant="ghost"
          color="gray"
          onClick={() => navigate("/quality/scorecards")}
        >
          <ArrowLeftIcon />
        </IconButton>

        <Text size="2" color="gray" weight="medium" highContrast>
          New Scorecard
        </Text>
      </Flex>

      <ScrollArea style={{ flexGrow: 1 }}>
        <Container size="4" style={{ paddingBlock: 32 }}>
          <SampleForm onSubmit={handleSubmit} loading={loading} />
        </Container>
      </ScrollArea>
    </div>
  );
}

export default CreateSamplePage;
